import {formatISO} from 'date-fns';
import {compact, find, merge, reject} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {UserAccountDto} from '../../../global/api';
import {useGetAccountsQuery} from '../../../global/services/accounts';
import {usePostCompaniesFindByIdsQuerySuspense, usePostCompaniesMutation} from '../../../global/services/companies';
import {TypeAccountRoles} from '../../../shared/types/user-account.types';
import {authState} from '../../../store/auth/atoms';
import {removeEmptyStrings} from '../helpers/remove-empty-strings';
import {CompanyProfile, ProfileDirectorOption, ProfileFormValues} from '../types/profile.types';

interface ProfileDataContext {
  loading: boolean;
  directorsLoading: boolean;
  directors: ProfileDirectorOption[];
  formData: ProfileFormValues;
  company: CompanyProfile | null;
  updateCompany: (values: ProfileFormValues) => Promise<boolean>;
}

export const useProfileData = (): ProfileDataContext => {
  const [loading, setLoading] = useState(false);
  const {user} = useRecoilValue(authState);
  const companyId = user?.companyId || '';
  const userUid = user?.uid || '';
  const {data, refresh: refreshCompany} = usePostCompaniesFindByIdsQuerySuspense(companyId ? [companyId] : []);
  const company = (data?.[0] as CompanyProfile) || null;
  const {data: directorsResponse, loading: directorsLoading} = useGetAccountsQuery({
    roles: [TypeAccountRoles.DIRECTOR],
    isActive: true,
    page: 0,
    size: 1000,
  });

  const directorsArray = useMemo(() => {
    const leader = company.leader;
    const content = directorsResponse?.content ?? [];
    const selected = find(content, {uid: leader?.uid});
    const mergedLeader = leader && selected?.uid === leader.uid ? merge({}, leader, selected) : leader;
    return compact([mergedLeader, ...reject(content, {uid: leader?.uid})]);
  }, [directorsResponse?.content, company.leader]);

  const directors = useMemo(
    () =>
      directorsArray
        .map((director: UserAccountDto) => {
          const fio =
            director.userFIO ||
            `${director.lastName || ''} ${director.firstName || ''} ${director.patronymic || ''}`.trim() ||
            'ФИО не задано';
          if (!director.uid || !fio) return null;
          return {
            uid: director.uid,
            fio,
          };
        })
        .filter((director): director is ProfileDirectorOption => Boolean(director)),
    [directorsArray],
  );
  const {mutate} = usePostCompaniesMutation();

  const formData = useMemo<ProfileFormValues>(
    () => ({
      payment: {
        rs: company?.payment?.rs || '',
        bankName: company?.payment?.bankName || '',
        bik: company?.payment?.bik || '',
        cor: company?.payment?.cor || '',
        id: company?.payment?.id,
      },
      fullCompanyName: company?.fullCompanyName || '',
      inn: company?.inn || '',
      ogrn: company?.ogrn || '',
      kpp: company?.kpp || '',
      website: company?.website || '',
      phone: company?.phone || '',
      email: company?.email || '',
      leaderUid: company?.leader?.uid || '',
      fullAddress: company?.address || null,
    }),
    [company],
  );

  const updateCompany = useCallback(
    async ({fullAddress, payment, leaderUid, ...companyFields}: ProfileFormValues) => {
      if (!company || !companyId) return false;

      const nowIso = formatISO(new Date());
      const selectedDirector = directorsArray.find((director) => director.uid === leaderUid);

      const normalizedPayment = {
        rs: payment.rs?.trim() || '',
        bankName: payment.bankName?.trim() || '',
        bik: payment.bik?.trim() || '',
        cor: payment.cor?.trim() || '',
        kpp: companyFields.kpp?.trim() || company.kpp?.trim() || '',
      };
      const hasPaymentDetails = Object.values(normalizedPayment).some(Boolean);

      const payload = removeEmptyStrings({
        ...company,
        ...companyFields,
        leader: leaderUid ? selectedDirector : undefined,
        leaderFIO: selectedDirector?.userFIO || company.leaderFIO,
        payment: hasPaymentDetails
          ? {
              ...(company.payment ?? {}),
              ...normalizedPayment,
              companyId: company.payment?.companyId || companyId,
              createdBy: company.payment?.createdBy || userUid,
              createdDate: company.payment?.createdDate || nowIso,
              lastModifiedBy: userUid,
              lastModifiedDate: nowIso,
            }
          : undefined,
        address: fullAddress ? {...company.address, ...fullAddress} : undefined,
      });

      let isSaved = false;

      try {
        setLoading(true);
        await mutate([payload], async () => {
          isSaved = true;
          refreshCompany();
        });
      } finally {
        setLoading(false);
      }

      return isSaved;
    },
    [company, companyId, directorsArray, mutate, refreshCompany, userUid],
  );

  return {
    loading,
    directorsLoading,
    directors,
    formData,
    company,
    updateCompany,
  };
};
